import React, { useEffect, useState } from "react";
import DashboardLayout from "../components/DashboardLayout";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast, Toaster } from "sonner";
import { Plus, Trash2, AlertTriangle } from "lucide-react";

export default function Settings() {
  const { user, tenant, refresh } = useAuth();
  const [form, setForm] = useState({
    name: tenant?.name || "",
    brand_color: tenant?.brand_color || "#0052FF",
    logo_url: tenant?.logo_url || "",
    support_email: tenant?.support_email || "",
  });
  const [loading, setLoading] = useState(false);
  const [carriers, setCarriers] = useState([]);
  const [cForm, setCForm] = useState({ carrier_code: "UPS", account_number: "", nickname: "", api_key: "" });

  const loadCarriers = async () => {
    try { const { data } = await api.get("/carrier-accounts"); setCarriers(data); }
    catch (e) { /* ignore */ }
  };
  useEffect(() => { loadCarriers(); }, []);

  useEffect(() => {
    if (tenant) setForm({
      name: tenant.name,
      brand_color: tenant.brand_color,
      logo_url: tenant.logo_url || "",
      support_email: tenant.support_email || "",
    });
  }, [tenant]);

  const save = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.put("/tenant/settings", form);
      toast.success("Settings saved");
      await refresh();
      document.documentElement.style.setProperty("--brand-accent", form.brand_color);
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setLoading(false); }
  };

  const canEdit = user?.role === "admin" || user?.role === "reseller";
  const isApproved = !user?.approval_status || user?.approval_status === "approved";

  const addCarrier = async (e) => {
    e.preventDefault();
    if (!cForm.account_number) { toast.error("Account number required"); return; }
    try {
      await api.post("/carrier-accounts", cForm);
      toast.success("Carrier account added");
      setCForm({ carrier_code: "UPS", account_number: "", nickname: "", api_key: "" });
      loadCarriers();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const removeCarrier = async (id) => {
    if (!window.confirm("Remove this carrier account?")) return;
    try { await api.delete(`/carrier-accounts/${id}`); toast.success("Removed"); loadCarriers(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <DashboardLayout>
      <Toaster position="top-right" richColors />
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">Settings</div>
        <h1 className="font-display text-3xl font-black tracking-tighter">Connections & account</h1>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        <div className="md:col-span-2 bg-white border border-zinc-200 rounded-sm p-6">
          <div className="font-display text-xl font-bold mb-1">Brand settings</div>
          <div className="text-sm text-zinc-600 mb-4">Customize how SellePack looks for your team and customers.</div>
          <form onSubmit={save} className="space-y-4">
            <Field label="Brand name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} disabled={!canEdit} testId="tenant-name" />
            <Field label="Support email" type="email" value={form.support_email} onChange={(v) => setForm({ ...form, support_email: v })} disabled={!canEdit} testId="tenant-support-email" />
            <Field label="Logo URL (optional)" value={form.logo_url} onChange={(v) => setForm({ ...form, logo_url: v })} disabled={!canEdit} testId="tenant-logo-url" />
            <div>
              <label className="text-xs uppercase tracking-wider text-zinc-500">Accent color</label>
              <div className="mt-1 flex items-center gap-3">
                <input
                  data-testid="tenant-brand-color"
                  type="color" value={form.brand_color}
                  onChange={(e) => setForm({ ...form, brand_color: e.target.value })}
                  disabled={!canEdit}
                  className="w-12 h-12 rounded-sm border border-zinc-300 cursor-pointer"
                />
                <input type="text" value={form.brand_color} onChange={(e) => setForm({ ...form, brand_color: e.target.value })} disabled={!canEdit}
                  className="border border-zinc-300 rounded-sm px-3 py-2 text-sm font-mono-plex w-32" />
                <div className="flex-1 py-2 rounded-sm text-white text-center text-sm font-medium" style={{ backgroundColor: form.brand_color }}>
                  Preview
                </div>
              </div>
            </div>
            {canEdit ? (
              <button data-testid="save-settings-btn" type="submit" disabled={loading} className="brand-bg text-white px-5 py-2 rounded-sm disabled:opacity-50">
                {loading ? "Saving…" : "Save settings"}
              </button>
            ) : (
              <div className="text-xs text-zinc-500">Only admins and resellers can edit tenant settings.</div>
            )}
          </form>
        </div>

        <div className="bg-white border border-zinc-200 rounded-sm p-6">
          <div className="font-display text-xl font-bold mb-4">Account</div>
          <Row label="Name" value={user?.name} />
          <Row label="Email" value={user?.email} />
          <Row label="Role" value={user?.role} />
          <Row label="Tenant" value={user?.tenant_id} />
          <Row label="Status" value={user?.approval_status || "approved"} />
          <Row label="Wallet" value={`$${(user?.wallet_balance || 0).toFixed(2)}`} />
        </div>
      </div>

      {/* Carrier accounts */}
      <div className="mt-6 bg-white border border-zinc-200 rounded-sm p-6" data-testid="carrier-accounts-section">
        <div className="flex items-start justify-between mb-4 flex-wrap gap-2">
          <div>
            <div className="font-display text-xl font-bold">Carrier accounts</div>
            <div className="text-sm text-zinc-600 mt-1">Load your own UPS, FedEx, DHL, or USPS accounts to ship under your rates — or use SellePack's default accounts.</div>
          </div>
        </div>
        {!isApproved && (
          <div className="bg-amber-50 border border-amber-200 rounded-sm p-4 mb-4 flex items-start gap-2" data-testid="carrier-pending-notice">
            <AlertTriangle size={16} className="text-amber-600 mt-0.5 shrink-0" />
            <div className="text-sm text-zinc-700">Your account is pending approval. Once approved you can add carrier credentials and start shipping.</div>
          </div>
        )}
        <form onSubmit={addCarrier} className="grid md:grid-cols-5 gap-3 items-end" data-testid="carrier-form">
          <div>
            <label className="text-xs uppercase tracking-wider text-zinc-500">Carrier</label>
            <select data-testid="carrier-code-select" value={cForm.carrier_code}
              onChange={(e) => setCForm({ ...cForm, carrier_code: e.target.value })}
              disabled={!isApproved}
              className="mt-1 w-full border border-zinc-300 rounded-sm px-3 py-2 text-sm disabled:bg-zinc-50">
              {[["UPS","UPS"],["FEDEX","FedEx"],["DHL","DHL"],["USPS","USPS"]].map(([v,l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-zinc-500">Account #</label>
            <input data-testid="carrier-account-number" value={cForm.account_number} disabled={!isApproved}
              onChange={(e) => setCForm({ ...cForm, account_number: e.target.value })}
              className="mt-1 w-full border border-zinc-300 rounded-sm px-3 py-2 text-sm disabled:bg-zinc-50" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-zinc-500">Nickname</label>
            <input data-testid="carrier-nickname" value={cForm.nickname} disabled={!isApproved}
              onChange={(e) => setCForm({ ...cForm, nickname: e.target.value })}
              className="mt-1 w-full border border-zinc-300 rounded-sm px-3 py-2 text-sm disabled:bg-zinc-50" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-zinc-500">API key (opt.)</label>
            <input data-testid="carrier-api-key" type="password" value={cForm.api_key} disabled={!isApproved}
              onChange={(e) => setCForm({ ...cForm, api_key: e.target.value })}
              className="mt-1 w-full border border-zinc-300 rounded-sm px-3 py-2 text-sm disabled:bg-zinc-50" />
          </div>
          <button data-testid="add-carrier-btn" type="submit" disabled={!isApproved}
            className="brand-bg text-white px-4 py-2 rounded-sm text-sm disabled:opacity-50 flex items-center justify-center gap-2">
            <Plus size={14} /> Add
          </button>
        </form>

        {carriers.length > 0 && (
          <div className="mt-5 space-y-2">
            {carriers.map((c) => (
              <div key={c.carrier_account_id} className="border border-zinc-200 rounded-sm p-3 flex items-center justify-between" data-testid={`carrier-account-${c.carrier_account_id}`}>
                <div>
                  <div className="font-display font-bold">{c.carrier_code} · {c.nickname || "account"}</div>
                  <div className="text-xs text-zinc-500 font-mono-plex">#{c.account_number}</div>
                </div>
                <button data-testid={`remove-carrier-${c.carrier_account_id}`} onClick={() => removeCarrier(c.carrier_account_id)}
                  className="text-zinc-500 hover:text-red-600"><Trash2 size={16} /></button>
              </div>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

function Field({ label, value, onChange, type = "text", disabled, testId }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-wider text-zinc-500">{label}</label>
      <input data-testid={testId} type={type} value={value || ""} disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full border border-zinc-300 rounded-sm px-3 py-2 focus:outline-none focus:border-[var(--brand-accent)] disabled:bg-zinc-50 disabled:text-zinc-500" />
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="py-2 border-b border-zinc-100 last:border-0 flex justify-between text-sm">
      <span className="text-zinc-500 uppercase tracking-wider text-xs">{label}</span>
      <span className="font-mono-plex">{value}</span>
    </div>
  );
}
